import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

@Component({
    selector: 'app-calendar',
    standalone: false,
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.css']
})
export class CalendarComponent implements OnInit {
    @Input() selectedDate: Date | null = null;
    @Output() dateSelect = new EventEmitter<Date | null>();

    viewMode: 'days' | 'months' | 'years' = 'days';
    viewDate: Date = new Date();
    days: { date: Date; isCurrentMonth: boolean; isSelected: boolean; isToday: boolean }[] = [];
    weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    years: number[] = [];

    ngOnInit() {
        if (this.selectedDate) {
            this.viewDate = new Date(this.selectedDate);
        }
        this.generateCalendar();
        this.generateYears();
    }

    generateYears(): void {
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - 100;
        this.years = Array.from({ length: 201 }, (_, i) => startYear + i);
    }

    showMonths(event: MouseEvent): void {
        event.stopPropagation();
        this.viewMode = this.viewMode === 'months' ? 'days' : 'months';
    }

    showYears(event: MouseEvent): void {
        event.stopPropagation();
        this.viewMode = this.viewMode === 'years' ? 'days' : 'years';
        if (this.viewMode === 'years') {
            this.scrollToActiveYear();
        }
    }

    private scrollToActiveYear(): void {
        setTimeout(() => {
            const activeYear = this.viewDate.getFullYear();
            const element = document.getElementById(`year-${activeYear}`);
            if (element) {
                element.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
        }, 100);
    }

    selectMonth(month: number, event: MouseEvent): void {
        event.stopPropagation();
        this.viewDate = new Date(this.viewDate.getFullYear(), month, 1);
        this.generateCalendar();
        this.viewMode = 'days';
    }

    selectYear(year: number, event: MouseEvent): void {
        event.stopPropagation();
        this.viewDate = new Date(year, this.viewDate.getMonth(), 1);
        this.generateCalendar();
        this.generateYears();
        this.viewMode = 'days';
    }

    onPrevClick(event: MouseEvent): void {
        event.stopPropagation();
        if (this.viewMode === 'days') {
            this.prevMonth();
        } else if (this.viewMode === 'months') {
            this.viewDate = new Date(this.viewDate.getFullYear() - 1, this.viewDate.getMonth(), 1);
        } else if (this.viewMode === 'years') {
            const currentStart = this.years[0];
            this.years = Array.from({ length: 201 }, (_, i) => currentStart - 50 + i);
        }
    }

    onNextClick(event: MouseEvent): void {
        event.stopPropagation();
        if (this.viewMode === 'days') {
            this.nextMonth();
        } else if (this.viewMode === 'months') {
            this.viewDate = new Date(this.viewDate.getFullYear() + 1, this.viewDate.getMonth(), 1);
        } else if (this.viewMode === 'years') {
            const currentStart = this.years[0];
            this.years = Array.from({ length: 201 }, (_, i) => currentStart + 50 + i);
        }
    }

    prevYear(event: MouseEvent): void {
        event.stopPropagation();
        const currentStart = this.years[0];
        this.years = Array.from({ length: 201 }, (_, i) => currentStart - 21 + i);
    }

    nextYear(event: MouseEvent): void {
        event.stopPropagation();
        const currentEnd = this.years[this.years.length - 1];
        this.years = Array.from({ length: 201 }, (_, i) => currentEnd + 1 + i);
    }

    generateCalendar(): void {
        const year = this.viewDate.getFullYear();
        const month = this.viewDate.getMonth();

        // First day of month
        const firstDay = new Date(year, month, 1);
        // Last day of month
        const lastDay = new Date(year, month + 1, 0);

        // Day of week for first day (0-6)
        const firstDayOfWeek = firstDay.getDay();

        const days = [];

        // Previous month's trailing days
        const prevMonthLastDay = new Date(year, month, 0);
        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
            const d = new Date(year, month - 1, prevMonthLastDay.getDate() - i);
            days.push(this.createDay(d, false));
        }

        // Current month's days
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const d = new Date(year, month, i);
            days.push(this.createDay(d, true));
        }

        // Next month's leading days
        const totalDaysRemaining = 42 - days.length; // 6 rows of 7 days
        for (let i = 1; i <= totalDaysRemaining; i++) {
            const d = new Date(year, month + 1, i);
            days.push(this.createDay(d, false));
        }

        this.days = days;
    }

    private createDay(date: Date, isCurrentMonth: boolean) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const d = new Date(date);
        d.setHours(0, 0, 0, 0);

        let isSelected = false;
        if (this.selectedDate) {
            const selected = new Date(this.selectedDate);
            selected.setHours(0, 0, 0, 0);
            isSelected = d.getTime() === selected.getTime();
        }

        return {
            date,
            isCurrentMonth,
            isSelected,
            isToday: d.getTime() === today.getTime()
        };
    }

    prevMonth(event?: MouseEvent): void {
        if (event) event.stopPropagation();
        this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
        this.generateCalendar();
    }

    nextMonth(event?: MouseEvent): void {
        if (event) event.stopPropagation();
        this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
        this.generateCalendar();
    }

    selectDate(date: Date, event: MouseEvent): void {
        event.stopPropagation();
        this.selectedDate = date;
        this.generateCalendar(); // Regenerate to update styles
        this.dateSelect.emit(date);
    }

    selectToday(event: MouseEvent): void {
        event.stopPropagation();
        const today = new Date();
        this.viewDate = new Date(today);
        this.selectDate(today, event);
    }

    clearDate(event: MouseEvent): void {
        event.stopPropagation();
        this.selectedDate = null;
        this.generateCalendar();
        this.dateSelect.emit(null);
    }
}
